import { Data, Effect } from 'effect';

/**
 * Shared Effect seam for the React data hooks.
 *
 * Every hook in `@okrapdf/sdk/react` performs the same shape of work: call one
 * async session method, then fold the outcome into React state. The async call
 * is modelled here as a native Effect program with a redacted tagged failure,
 * and the hooks stay on the Promise side through {@link runOkraHookEffect},
 * which restores the ORIGINAL rejection value so existing `catch` blocks
 * (`err.name === 'AbortError'`, `err instanceof Error`) keep working verbatim.
 */
export type OkraHookOperation =
  | 'documentStatus'
  | 'pages'
  | 'pageContent'
  | 'documentQuery'
  | 'assetQuery'
  | 'chatPrompt'
  | 'authEndpoint'
  | 'providerSession'
  | 'assetPlugins';

/**
 * Safe native failure: document bytes, prompts, response bodies, and API keys
 * are never fields. Only the operation label and a fixed message travel with
 * the tagged error; the original cause stays in a side table.
 */
export class OkraHookError extends Data.TaggedError('OkraHookError')<{
  readonly operation: OkraHookOperation;
  readonly message: string;
}> {}

const hookErrorCauses = new WeakMap<OkraHookError, unknown>();

export function okraHookFailure(
  operation: OkraHookOperation,
  message: string,
  cause: unknown,
): OkraHookError {
  const error = new OkraHookError({ operation, message });
  hookErrorCauses.set(error, cause);
  return error;
}

/** Original compatibility cause without exposing it on the safe tagged failure. */
export function okraHookErrorCause(error: OkraHookError): unknown {
  return hookErrorCauses.get(error) ?? error;
}

export function restoreOkraHookError<A>(
  effect: Effect.Effect<A, OkraHookError>,
): Effect.Effect<A, unknown> {
  return effect.pipe(Effect.catch((error) => Effect.fail(okraHookErrorCause(error))));
}

/** Promise adapter used by the hooks: rejects with the original cause. */
export function runOkraHookEffect<A>(
  effect: Effect.Effect<A, OkraHookError>,
  signal?: AbortSignal,
): Promise<A> {
  return Effect.runPromise(Effect.andThen(Effect.yieldNow, restoreOkraHookError(effect)), {
    signal,
  });
}
